import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Download, 
  Search,
  Eye,
  Edit3,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface TaxForm {
  id: string;
  formType: string;
  formName: string;
  entity: string;
  taxYear: string;
  status: 'draft' | 'review' | 'filed' | 'amended';
  dueDate: string;
  filedDate?: string;
  completionPercentage: number;
  lastModified: string;
}

const WealthOSForms: React.FC = () => {
  const [forms, setForms] = useState<TaxForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedYear, setSelectedYear] = useState('2025');

  useEffect(() => {
    const loadForms = async () => {
      setLoading(true);
      
      const mockForms: TaxForm[] = [
        {
          id: '1',
          formType: '1120',
          formName: 'U.S. Corporation Income Tax Return',
          entity: 'TechCorp LLC',
          taxYear: '2024',
          status: 'filed',
          dueDate: '2025-03-15',
          filedDate: '2025-03-10',
          completionPercentage: 100,
          lastModified: '2025-03-10'
        },
        {
          id: '2',
          formType: '1065',
          formName: 'U.S. Return of Partnership Income',
          entity: 'Investment Partners',
          taxYear: '2024',
          status: 'review',
          dueDate: '2025-03-15',
          completionPercentage: 95,
          lastModified: '2025-01-20'
        },
        {
          id: '3',
          formType: '1040',
          formName: 'U.S. Individual Income Tax Return',
          entity: 'John Smith',
          taxYear: '2024',
          status: 'draft',
          dueDate: '2025-04-15',
          completionPercentage: 60,
          lastModified: '2025-01-25'
        },
        {
          id: '4',
          formType: 'Schedule K-1',
          formName: 'Partner\'s Share of Income, Deductions, Credits, etc.',
          entity: 'Investment Partners',
          taxYear: '2024',
          status: 'amended',
          dueDate: '2025-03-15',
          filedDate: '2025-03-12',
          completionPercentage: 100,
          lastModified: '2025-03-18'
        }
      ];

      setForms(mockForms);
      setLoading(false);
    };

    loadForms();
  }, [selectedYear, selectedStatus]);

  const filteredForms = forms.filter(form => {
    const matchesSearch = form.formType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         form.formName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         form.entity.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || form.status === selectedStatus;
    const matchesYear = selectedYear === 'all' || form.taxYear === selectedYear;
    
    return matchesSearch && matchesStatus && matchesYear;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'filed':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'review':
        return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      case 'draft':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'amended':
        return 'text-purple-400 bg-purple-500/20 border-purple-500/30';
      default:
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'filed':
        return <CheckCircle className="w-4 h-4" />;
      case 'review':
        return <Eye className="w-4 h-4" />;
      case 'draft':
        return <Edit3 className="w-4 h-4" />;
      case 'amended':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const handleCreateForm = () => {
    console.log('Creating new form...');
  };

  const handleEditForm = (formId: string) => {
    console.log('Editing form:', formId);
  };

  const handleViewForm = (formId: string) => {
    console.log('Viewing form:', formId);
  };

  const handleDownloadForm = (formId: string) => {
    console.log('Downloading form:', formId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading tax forms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-900 p-6 overflow-auto">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-500/20 rounded-lg">
              <FileText className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white text-drop-shadow">Tax Forms</h1>
              <p className="text-slate-400">View, edit, and download IRS tax forms</p>
            </div>
          </div>
          
          <button
            onClick={handleCreateForm}
            className="px-4 py-2 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 rounded-lg text-teal-400 font-medium transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Form
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search forms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="review">Review</option>
              <option value="filed">Filed</option>
              <option value="amended">Amended</option>
            </select>
            
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            >
              <option value="all">All Years</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {filteredForms.map((form, index) => (
            <motion.div
              key={form.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <FileText className="w-6 h-6 text-slate-300" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{form.formType}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(form.status)}`}>
                        {getStatusIcon(form.status)}
                        {form.status}
                      </span>
                    </div>
                    
                    <p className="text-slate-300 mb-2">{form.formName}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span>{form.entity}</span>
                      <span>•</span>
                      <span>Tax Year {form.taxYear}</span>
                      <span>•</span>
                      <span>Due {form.dueDate}</span>
                      {form.filedDate && (
                        <>
                          <span>•</span>
                          <span>Filed {form.filedDate}</span>
                        </>
                      )}
                    </div>
                    
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-400">Completion</span>
                        <span className="text-slate-300">{form.completionPercentage}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-teal-400 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${form.completionPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleViewForm(form.id)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  
                  {form.status !== 'filed' && (
                    <button
                      onClick={() => handleEditForm(form.id)}
                      className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleDownloadForm(form.id)}
                    className="px-3 py-2 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 rounded-lg text-teal-400 text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredForms.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No forms found</h3>
            <p className="text-slate-500">Try adjusting your search criteria or create a new form.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WealthOSForms;
